import { onlineManager } from '@tanstack/react-query';
import { isNative, registerNativeListener } from '@/shared/bridge';
import { UI_TEXT } from '@/shared/languages';
import { toast } from '@/shared/lib';

/**
 * 온라인 상태의 **단일 코디네이터** — 두 신호원을 한 곳에서 합류시킨다(`fcm-sync` 와 같은 형태):
 *   ① 브라우저 `online`/`offline` 이벤트 — 데스크톱·모바일 브라우저의 기본 신호
 *   ② 네이티브 `network.change` 브릿지 메시지 — WebView 전용 보정
 *
 * **왜 둘 다인가**: WebView 안의 `navigator.onLine` 은 실제 연결을 못 따라가는 경우가 많다(특히 iOS —
 * 비행기모드 해제 후에도 갱신이 늦거나 안 옴). 그렇다고 네이티브 신호만 쓰면 브라우저에서 아무 신호도
 * 못 받는다. 그래서 브라우저 이벤트는 **항상** 구독하고, 네이티브에서만 브릿지 신호를 **추가**로 얹는다
 * (나중에 온 신호가 이긴다 — 네이티브가 OS 판정이라 더 정확하고, 부팅 시 현재 상태를 1회 밀어준다).
 *
 * 합류점은 TanStack `onlineManager` 하나 — 여기에 물리면 오프라인 중 쿼리는 `paused` 로 대기하다가
 * 복귀 시 자동 재개된다(재시도 카운터 소모 없음). 상태 소비처가 늘어도 이 매니저만 구독하면 된다.
 */
export function initNetworkSync(): void {
  // 기본 리스너(브라우저 이벤트만)를 교체 — 반환 함수가 해제 책임을 진다.
  onlineManager.setEventListener((setOnline) => {
    const unsubscribers: Array<() => void> = [];

    // ① 브라우저 — 네이티브에서도 구독한다. 신호가 틀릴 수는 있어도, 있는 신호를 버릴 이유는 없다
    //    (네이티브 신호가 뒤이어 정정한다).
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    unsubscribers.push(() => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    });

    // ② 네이티브 — 브릿지가 있을 때만. `registerNativeListener` 가 해제 함수를 돌려준다.
    if (isNative()) {
      unsubscribers.push(
        registerNativeListener('network.change', (msg) => setOnline(msg.payload.online)),
      );
    }

    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
  });

  // 오프라인 **전환 시점에만** 1회 알림. 매니저는 같은 값을 여러 번 통지할 수 있어(신호원 2개라 더욱)
  // 직전 값과 비교해 중복 토스트를 막는다. 복귀는 화면이 알아서 다시 차오르므로 별도 알림을 두지 않는다.
  let previousOnline = onlineManager.isOnline();
  onlineManager.subscribe((online) => {
    if (online === previousOnline) return;
    previousOnline = online;
    if (!online) toast.error(UI_TEXT.NETWORK_OFFLINE);
  });
}
